// src/pages/school/fees/FeeLogin.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Button, Form, InputGroup, Alert } from "react-bootstrap";
import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";
import {
  isFeeUnlocked,
  setFeeUnlocked,
  verifyFeePin,
  fetchFeeConfig,
} from "./data/feesAuth";

/**
 * PIN unlock page for Fees module.
 * - If already unlocked (valid token), auto-redirects.
 * - If no PIN is configured, auto-redirects.
 * - Supports "remember on this device" (24h).
 */
export default function FeeLogin() {
  const { school } = useSchool();
  const { user, isSchoolAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();

  const schoolId = school?.id || null;
  const uid = user?.uid || null;
  const next = new URLSearchParams(search).get("next") || "/school/fees";

  const [pin, setPin] = useState("");
  const [hint, setHint] = useState("");
  const [remember, setRemember] = useState(true);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");

  // Initial checks + hint
  useEffect(() => {
    (async () => {
      if (authLoading) return;
      if (!schoolId || !uid) { setChecking(false); return; }

      // non-admin = block (but keep UI graceful)
      if (!isSchoolAdmin) { setChecking(false); return; }

      // Already unlocked?
      if (isFeeUnlocked(schoolId, uid)) {
        navigate(next, { replace: true });
        return;
      }

      // Fetch hint or auto-allow if no pin set
      const cfg = await fetchFeeConfig(schoolId).catch(() => null);
      if (!cfg?.pinHash) {
        // No PIN set — allow straight through
        setFeeUnlocked(schoolId, uid, { remember: true });
        navigate(next, { replace: true });
        return;
      }

      setHint(cfg?.hint || "");
      setChecking(false);
    })();
  }, [authLoading, schoolId, uid, isSchoolAdmin, next, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!schoolId || !uid) {
      setError("Missing school or user context.");
      return;
    }
    if (!isSchoolAdmin) {
      setError("Only School Admins can unlock Fees.");
      return;
    }
    if (!pin.trim()) {
      setError("Enter your PIN.");
      return;
    }

    const res = await verifyFeePin({ schoolId, pin });
    if (res.ok) {
      setFeeUnlocked(schoolId, uid, { remember });
      navigate(next, { replace: true });
    } else {
      setError("Incorrect PIN. Try again.");
    }
  };

  if (checking) {
    return (
      <div className="container py-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-5 text-center text-muted">Checking lock…</Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ maxWidth: 520 }}>
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <h5 className="mb-2">Unlock Fees</h5>
          <div className="text-muted small mb-3">
            {school?.name || "School"} • Admin access
          </div>

          {!isSchoolAdmin && (
            <Alert variant="warning">
              Only School Admins can unlock the Fees module.
            </Alert>
          )}

          {hint ? (
            <Alert variant="light" className="border">
              Hint: {hint}
            </Alert>
          ) : null}

          {error ? <Alert variant="danger">{error}</Alert> : null}

          <Form onSubmit={onSubmit}>
            <Form.Label>PIN</Form.Label>
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <i className="bi bi-shield-lock" />
              </InputGroup.Text>
              <Form.Control
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                autoFocus
                placeholder="Enter Fee PIN"
              />
            </InputGroup>

            <Form.Check
              className="mb-3"
              label="Remember on this device (24 hours)"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />

            <div className="d-flex gap-2">
              <Button type="submit" disabled={!isSchoolAdmin}>
                Unlock
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate("/school", { replace: true })}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}
